import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import Icon from '../../app/common/components/Icon';
import ICONS from '../../app/common/constants/icons';
import Spinner from '../../app/common/components/Spinner';
import { COLORS, MQS } from '../../app/common/constants/styles';
import { TranslationHelpers } from '../../i18n/translationHelpers';
import { getTranslatedProviderName, getTranslatedServiceName } from './utils/translationMapper';
import { LinkedService, SocialProvider, UnlinkModalState } from './types/socialAccounts.types';
import ServiceCard from './components/ServiceCard';
import UnlinkConfirmationModal from '../../app/common/components/UnlinkConfirmationModal';
import { fetchServicesConfig, unlinkSocialProvider } from './api/socialAccounts.api';
import { SocialAccountRatEvents } from './utils/socialAccountRatEvents';
import { history } from '../../utils/history';
import { useDispatch } from 'react-redux';
import { loginActions } from '../../login/login.actions';

const PageWrapper = styled.div`
    max-width: 760px;
    margin: 0 auto;
    padding: 1.5rem;

    @media (max-width: 768px) {
        padding: 1rem;
    }
`;

const PageTitle = styled.h1`
    font-size: 1.75rem;
    font-weight: 400;
    color: ${COLORS.dark};
    margin: 18px auto 26px 0;

    @media (max-width: ${MQS.mq2Max}) {
        display: none;
    }
`;

const Subtitle = styled.div`
    font-weight: 600;
    color: ${COLORS.dark};
    margin-bottom: 1.5rem;
    font-size: 24px;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
`;

const InfoBanner = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    background-color: #ebf7fe;
    border-radius: 8px;
    padding: 12px;
    margin: 24px 0;
    color: #006497;
    font-size: 16px;
    line-height: 24px;
`;

const InfoIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
`;

const ServicesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2rem;
`;

// Map provider IDs to friendly names using partial matching
// Provider IDs follow the pattern <prefix><provider> (e.g. amukgoogle, travelglobalkoreagoogle)
const getProviderName = (providerId: string): 'Google' | 'Facebook' | 'Apple' | null => {
    const id = providerId.toLowerCase();
    if (id.includes('google')) return 'Google';
    if (id.includes('facebook')) return 'Facebook';
    if (id.includes('apple')) return 'Apple';
    return null;
};

// Frontend mapping for service logos (AP region)
const SERVICE_LOGO_MAP: Record<string, string> = {
    'Rakuten Travel Taiwan': '/assets/logo/logo_rakuten_travel_taiwan.svg',
    'Rakuten Travel Global': '/assets/logo/logo_rakuten_travel_global.svg',
    'Rakuten Travel Korea': '/assets/logo/logo_rakuten_travel_global.svg',
};

// Map backend client IDs to user-friendly display names (English)
// These names are then passed to translation system for localization
const CLIENT_ID_TO_DISPLAY_NAME: Record<string, string> = {
    travel_global_korea_web: 'Rakuten Travel Korea',
    travel_standard_taiwan_web: 'Rakuten Travel Taiwan',
    travel_standard_global_web: 'Rakuten Travel Global',
};

interface SocialAccountsPageAPProps extends InjectedIntlProps {
    showSuccessNotification: (message: string) => void;
    showErrorNotification: (message: string) => void;
    appConfig: {
        SOCIAL_ACCOUNT_MANAGEMENT_ENABLED: boolean;
        CHALLENGER_PID: string;
    };
}

const SocialAccountsPageAP: FC<SocialAccountsPageAPProps> = ({
    intl,
    showSuccessNotification,
    showErrorNotification,
    appConfig,
}) => {
    const [loading, setLoading] = useState(true);
    const [unlinking, setUnlinking] = useState(false);
    const [services, setServices] = useState<LinkedService[]>([]);
    const [modal, setModal] = useState<UnlinkModalState>({
        isOpen: false,
        provider: null,
        serviceName: '',
        unlinkTargetId: '',
    });
    const dispatch = useDispatch();

    useEffect(() => {
        if (appConfig.SOCIAL_ACCOUNT_MANAGEMENT_ENABLED) {
            loadData();
        }
    }, [appConfig.SOCIAL_ACCOUNT_MANAGEMENT_ENABLED]);

    // Check if feature is enabled
    if (!appConfig.SOCIAL_ACCOUNT_MANAGEMENT_ENABLED) {
        return null;
    }

    const loadData = async (): Promise<LinkedService[] | undefined> => {
        try {
            setLoading(true);

            // Fetch services config (already filtered to user's services)
            const servicesConfigData = await fetchServicesConfig();

            // Transform data into LinkedService format
            const linkedServices: LinkedService[] = [];

            // Loop through services in config
            Object.entries(servicesConfigData).forEach(([serviceUuid, serviceConfig]) => {
                if (!serviceConfig) return;

                const socialProviders: SocialProvider[] = [];

                // Add each linked social provider (if any)
                if (serviceConfig.social) {
                    Object.entries(serviceConfig.social).forEach(([providerType, providerData]) => {
                        if (!providerData) return;

                        const providerName = getProviderName(providerData.providerId);
                        if (providerName) {
                            socialProviders.push({
                                providerId: providerData.providerId,
                                providerName,
                                email: providerData.email || '',
                            });
                        }
                    });

                    // Sort providers in order: Google, Apple, Facebook
                    const providerOrder: Record<string, number> = {
                        Google: 1,
                        Apple: 2,
                        Facebook: 3,
                    };
                    socialProviders.sort(
                        (a, b) =>
                            (providerOrder[a.providerName] || 99) -
                            (providerOrder[b.providerName] || 99),
                    );
                }

                // Get raw name from backend (usually client ID like 'travel_global_korea_web')
                const rawName = serviceConfig.serviceName || serviceConfig.name || serviceUuid;

                // Map client ID to display name for translation system
                const serviceName = CLIENT_ID_TO_DISPLAY_NAME[rawName] || rawName;

                // Use hardcoded logo mapping for AP region (backend logo fields are for EU region only)
                const serviceLogoUrl = SERVICE_LOGO_MAP[serviceName];

                // Only add services that have at least one social provider linked
                if (socialProviders.length > 0) {
                    linkedServices.push({
                        serviceUuid,
                        serviceName,
                        rawName,
                        serviceLogoUrl,
                        socialProviders,
                    });
                }
            });

            setServices(linkedServices);
            return linkedServices;
        } catch (error) {
            console.error('Failed to load linked accounts:', error);
            showErrorNotification(
                intl.formatMessage({
                    id: 'notifications.social.unlink.failure',
                    defaultMessage:
                        'There was a problem submitting your request, please try again later.',
                }),
            );
        } finally {
            setLoading(false);
        }
    };

    const handleUnlinkClick = (
        provider: SocialProvider,
        serviceName: string,
        unlinkTargetId: string,
    ) => {
        setModal({
            isOpen: true,
            provider,
            serviceName,
            unlinkTargetId,
        });
    };

    const handleUnlinkConfirm = async () => {
        if (!modal.provider) return;

        const providerName = modal.provider.providerName;

        try {
            setUnlinking(true);

            // Track confirm action before API call
            SocialAccountRatEvents.trackUnlinkConfirm(providerName, modal.unlinkTargetId);
            await unlinkSocialProvider(modal.provider.providerId, appConfig.CHALLENGER_PID);

            // Track successful unlink after BE responds successfully
            SocialAccountRatEvents.trackUnlinkSuccessful(providerName, modal.unlinkTargetId);

            const translatedProviderName = getTranslatedProviderName(
                modal.provider.providerName,
                intl,
            );

            showSuccessNotification(
                intl.formatMessage(
                    {
                        id: 'notifications.social.unlink.success',
                        defaultMessage: 'Your {provider} account was successfully unlinked.',
                    },
                    { provider: translatedProviderName },
                ),
            );

            // Close modal and reload data
            setModal({ isOpen: false, provider: null, serviceName: '', unlinkTargetId: '' });
            const remainingServices = await loadData();
            if (remainingServices && remainingServices.length === 0) {
                dispatch(loginActions.getMember());
                history.push('/');
            }
        } catch (error) {
            console.error('Failed to unlink account:', error);
            showErrorNotification(
                intl.formatMessage({
                    id: 'notifications.social.unlink.failure',
                    defaultMessage:
                        'There was a problem submitting your request, please try again later.',
                }),
            );
        } finally {
            setUnlinking(false);
        }
    };

    const handleUnlinkCancel = () => {
        if (modal.provider) {
            SocialAccountRatEvents.trackUnlinkCancel(
                modal.provider.providerName,
                modal.unlinkTargetId,
            );
        }
        setModal({ isOpen: false, provider: null, serviceName: '', unlinkTargetId: '' });
    };

    const serviceName = getTranslatedServiceName(modal.serviceName, intl);
    const providerName = modal.provider
        ? getTranslatedProviderName(modal.provider.providerName, intl)
        : '';

    if (loading) {
        return (
            <PageWrapper>
                <PageTitle>
                    {TranslationHelpers.getFormattedMessage('linked.accounts.page.title')}
                </PageTitle>
                <Subtitle>
                    {TranslationHelpers.getFormattedMessage('linked.accounts.subtitle')}
                </Subtitle>
                <InfoBanner>
                    <InfoIconWrapper>
                        <Icon icon={ICONS.INFO_FILLED} size="16" viewBoxMax="16" fill="#006497" />
                    </InfoIconWrapper>
                    {TranslationHelpers.getFormattedMessage('linked.accounts.info')}
                </InfoBanner>
                <LoadingContainer>
                    <Spinner size={50} />
                </LoadingContainer>
            </PageWrapper>
        );
    }

    if (services.length === 0) {
        return (
            <PageWrapper>
                <PageTitle>
                    {TranslationHelpers.getFormattedMessage('linked.accounts.page.title')}
                </PageTitle>
                <Subtitle>
                    {TranslationHelpers.getFormattedMessage('linked.accounts.subtitle')}
                </Subtitle>
                <InfoBanner>
                    <InfoIconWrapper>
                        <Icon icon={ICONS.INFO_FILLED} size="16" viewBoxMax="16" fill="#006497" />
                    </InfoIconWrapper>
                    {TranslationHelpers.getFormattedMessage('linked.accounts.info')}
                </InfoBanner>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            <PageTitle>
                {TranslationHelpers.getFormattedMessage('linked.accounts.page.title')}
            </PageTitle>
            <Subtitle>
                {TranslationHelpers.getFormattedMessage('linked.accounts.subtitle')}
            </Subtitle>
            <InfoBanner>
                <InfoIconWrapper>
                    <Icon icon={ICONS.INFO_FILLED} size="16" viewBoxMax="16" fill="#006497" />
                </InfoIconWrapper>
                {TranslationHelpers.getFormattedMessage('linked.accounts.info')}
            </InfoBanner>
            <ServicesList>
                {services.map((service) => (
                    <ServiceCard
                        key={service.serviceUuid}
                        service={service}
                        unlinkTargetId={service.rawName}
                        onUnlinkProvider={handleUnlinkClick}
                        disabled={unlinking}
                    />
                ))}
            </ServicesList>

            <UnlinkConfirmationModal
                isOpen={modal.isOpen}
                providerName={providerName}
                serviceName={<strong>{serviceName}</strong>}
                unlinking={unlinking}
                onConfirm={handleUnlinkConfirm}
                onClose={handleUnlinkCancel}
                challengerPid={appConfig.CHALLENGER_PID}
            />
        </PageWrapper>
    );
};

export default injectIntl(SocialAccountsPageAP);
